<template>
    <card-list>
        <subheader-list-item icon="fa-align-left" title="Type Information"/>

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column">
                    <b-field label="Title" label-position="inside">
                        <b-input v-model="type.title"></b-input>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Price" label-position="inside">
                        <b-input v-model="type.fee" placeholder="0.00"></b-input>
                    </b-field>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-align-left" title="Enable Settings"/>

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column">
                    <b-field>
                        <b-switch v-model="type.is_enabled" true-value="1" false-value="0">Enabled for Registration</b-switch>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Enabled Date (optional)" label-position="inside">
                        <b-datetimepicker
                            v-model="type.enabled_at"
                            placeholder="Click to set..."
                            trap-focus
                            append-to-body
                            expanded
                        >
                        </b-datetimepicker>
                        <p class="control">
                            <b-button type="is-warning" outlined @click="type.enabled_at = null"><i class="fas fa-times ml-1 mr-1"></i></b-button>
                        </p>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Disabled Date" label-position="inside">
                        <b-datetimepicker
                            v-model="type.disabled_at"
                            placeholder="Click to set..."
                            trap-focus
                            append-to-body
                            expanded
                        >
                        </b-datetimepicker>
                        <p class="control">
                            <b-button type="is-warning" outlined @click="type.disabled_at = null"><i class="fas fa-times ml-1 mr-1"></i></b-button>
                        </p>
                    </b-field>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-map-marker-alt" title="Tax Settings (Physical Location)"/>

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column">
                    <p class="mb-3 has-text-grey-dark">For physical conference locations, specify an address to determine the applicable tax rate. Leave blank for virtual conferences (tax will be calculated from user's address).</p>
                </div>
            </div>
            <div class="columns">
                <div class="column is-6">
                    <b-field label="Address" label-position="inside">
                        <b-input v-model="taxAddress.address" placeholder="123 Main St"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="City" label-position="inside">
                        <b-input v-model="taxAddress.city" placeholder="Seattle"></b-input>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column is-4">
                    <b-field label="State" label-position="inside">
                        <b-input v-model="taxAddress.state" placeholder="WA" maxlength="2"></b-input>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Zip Code" label-position="inside">
                        <b-input v-model="taxAddress.zip" placeholder="98101"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-button @click="lookupTaxRate" type="is-primary" :loading="taxLookupLoading" :disabled="!canLookupTax">
                        <i class="fas fa-search mr-2"></i>Lookup Tax Rate
                    </b-button>
                </div>
            </div>

            <div v-if="taxLookupError" class="columns">
                <div class="column">
                    <b-message type="is-danger">{{ taxLookupError }}</b-message>
                </div>
            </div>

            <div v-if="type.tax_rate !== null && type.tax_rate !== undefined" class="columns">
                <div class="column">
                    <b-message type="is-success">
                        <strong>Tax Rate Configured:</strong> {{ (parseFloat(type.tax_rate) * 100).toFixed(3) }}%
                        (State: {{ (parseFloat(type.tax_state_rate || 0) * 100).toFixed(3) }}%, Local: {{ (parseFloat(type.tax_local_rate || 0) * 100).toFixed(3) }}%)
                        <br>
                        <strong>Jurisdiction:</strong> {{ type.tax_jurisdiction || 'N/A' }}
                    </b-message>
                </div>
            </div>

            <div v-if="type.tax_rate !== null && type.tax_rate !== undefined" class="columns">
                <div class="column">
                    <b-button @click="clearTaxRate" type="is-warning" outlined size="is-small">
                        <i class="fas fa-times mr-2"></i>Clear Tax
                    </b-button>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-align-left" title="Advanced Settings"/>

        <container-list-item padded no-border>
            <div class="columns">
                <div class="column is-4">
                    <b-field label="Credit Card Surcharge" label-position="inside">
                        <b-input v-model="type.cc_surcharge" placeholder="0.00"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Surcharge Info" label-position="inside">
                        <b-input v-model="type.cc_surcharge_info"></b-input>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="CC Fee" label-position="inside">
                        <b-input v-model="type.cc_fee" placeholder="0.00"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="PO Fee" label-position="inside">
                        <b-input v-model="type.po_fee" placeholder="0.00"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Org Share" label-position="inside">
                        <b-input v-model="type.org_share" placeholder="0.00"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="24/7 Share" label-position="inside">
                        <b-input v-model="type.self_share" placeholder="0.00"></b-input>
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column is-4">
                    <b-field label="Hour Limit" label-position="inside">
                        <b-input v-model="type.hour_limit" placeholder="0.00"></b-input>
                    </b-field>
                </div>
            </div>
        </container-list-item>
    </card-list>
</template>

<script>
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import CardListItem from "@/TIER/components/CardListItem";
    import SeparatorListItem from "@/components/common/SeparatorListItem";
    import {client as http} from "@/http_client";

    export default {
        name: 'ConfTypeForm',
        props: {
            type: {
                type: Object,
                required: true
            },
            conferenceId: {
                type: [Number, String],
                required: false
            }
        },
        components: {SeparatorListItem, CardListItem, ContainerListItem, SubheaderListItem, CardList},
        data: function() {
            return {
                taxAddress: {
                    address: '',
                    city: '',
                    state: 'WA',
                    zip: ''
                },
                taxLookupLoading: false,
                taxLookupError: null
            };
        },
        computed: {
            canLookupTax() {
                return this.taxAddress.address && this.taxAddress.city && this.taxAddress.state && this.taxAddress.zip;
            }
        },
        methods: {
            async lookupTaxRate() {
                if (!this.canLookupTax) return;

                this.taxLookupLoading = true;
                this.taxLookupError = null;

                try {
                    const response = await http.post(`/conferences/${this.conferenceId}/types/taxLookup`, this.taxAddress);

                    if (response.data.success) {
                        this.type.tax_rate = response.data.taxRate;
                        this.type.tax_state_rate = response.data.stateRate;
                        this.type.tax_local_rate = response.data.localRate;
                        this.type.tax_jurisdiction = response.data.jurisdiction;
                        this.type.tax_location_code = response.data.locationCode;
                    } else {
                        this.taxLookupError = response.data.error || 'Failed to lookup tax rate';
                    }
                } catch (error) {
                    console.error('Tax lookup error:', error);
                    this.taxLookupError = error.response?.data?.error || 'An error occurred while looking up tax rate';
                } finally {
                    this.taxLookupLoading = false;
                }
            },
            clearTaxRate() {
                this.type.tax_rate = null;
                this.type.tax_state_rate = null;
                this.type.tax_local_rate = null;
                this.type.tax_jurisdiction = null;
                this.type.tax_location_code = null;

                this.$buefy.toast.open({
                    message: 'Tax rate cleared',
                    type: 'is-info'
                });
            }
        }
    };
</script>
